import { useCallback, useMemo } from 'react';

import type { SymbolKey } from '@/entities/symbol';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { SELECTOR_MODES } from '../constants';
import {
  deleteSymbol,
  deleteSymbols,
  requestAddForwardCurves,
  requestSpotSelection
} from '../state/symbolSelector.slice';

export const useSelectionFunctions = () => {
  const dispatch = useAppDispatch();
  const symbols = useAppSelector(state => state.symbolSelector.symbols);
  const selectorMode = useAppSelector(state => state.symbolSelector.selectorMode);

  const isSpotLocked = selectorMode === SELECTOR_MODES.FORWARD_CURVE;
  const isForwardCurveLocked = selectorMode === SELECTOR_MODES.SPOT;

  const toggleSymbolInSelection = useCallback(
    (symbol: SymbolKey): void => {
      dispatch(requestSpotSelection({ symbol }));
    },
    [dispatch]
  );

  const selectedKeySet = useMemo(() => {
    const toKey = (s: SymbolKey) =>
      isSpotLocked
        ? `${s.Code}|${s.PeriodType}|${s.TimeRef}`
        : `${s.Code}|${s.Period}|${s.PeriodType}|${s.TimeRef}`;
    const set = new Set<string>();
    for (const s of symbols) set.add(toKey(s));
    return { set, toKey } as const;
  }, [symbols, isSpotLocked]);

  const isSymbolSelected = useCallback(
    (selectedSymbol: SymbolKey | undefined) => {
      if (!selectedSymbol) return false;
      return selectedKeySet.set.has(selectedKeySet.toKey(selectedSymbol));
    },
    [selectedKeySet]
  );

  const areAllSymbolsSelected = useCallback(
    (arr: SymbolKey[]) => {
      return arr.every(symbol => selectedKeySet.set.has(selectedKeySet.toKey(symbol)));
    },
    [selectedKeySet]
  );

  const addSymbolToSelection = (symbol: SymbolKey) => {
    dispatch(requestSpotSelection({ symbol }));
  };

  const addSymbolsToSelection = (newSymbols: SymbolKey[]) => {
    dispatch(requestSpotSelection({ symbols: newSymbols }));
  };

  const addForwardCurvesToSelection = (newSymbols: SymbolKey[]) => {
    dispatch(requestAddForwardCurves({ symbols: newSymbols }));
  };

  const removeSymbolsFromSelection = (newSymbols: Array<SymbolKey>) => {
    dispatch(deleteSymbols({ symbols: newSymbols }));
  };

  const removeSymbolFromSelection = (symbol: SymbolKey) => {
    if (symbol.Code.length) {
      dispatch(deleteSymbol({ symbol }));
    }
  };

  const noneSelected = symbols.length < 1;

  return {
    toggleSymbolInSelection,
    addSymbolsToSelection,
    addForwardCurvesToSelection,
    areAllSymbolsSelected,
    removeSymbolFromSelection,
    removeSymbolsFromSelection,
    isSymbolSelected,
    addSymbolToSelection,
    noneSelected,
    isSpotLocked,
    isForwardCurveLocked
  };
};
